import React, { useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import type { Article, Settings } from '../../types';
import { useDocumentBySlug } from '../../hooks/useDocumentBySlug';
import OptimizedImage from '../../components/OptimizedImage';
import { useSEO } from '../../hooks/useSEO';

interface ArticleDetailPageProps {
    settings: Settings;
}

const ArticleDetailPage: React.FC<ArticleDetailPageProps> = ({ settings }) => {
    const { slug } = useParams<{ slug: string }>();

    const { document: article, isLoading } = useDocumentBySlug<Article>('articles', slug);

    useSEO({
        title: article ? `${article.title} | ${settings.companyName}` : undefined,
        description: article?.excerpt,
        ogImage: article?.imageUrl
    }, [article, settings]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]);

    if(isLoading) {
        return <div className="h-screen flex items-center justify-center"><p>Loading article...</p></div>;
    }

    if (!article) {
        return <Navigate to="/journal" />;
    }
    
    const formattedDate = new Date(article.publishDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });

    return (
        <div className="bg-brand-light text-brand-dark pb-16">
            
            <div className="relative">
                <div className="h-[60vh] w-full bg-brand-dark">
                    <OptimizedImage 
                        src={article.imageUrl}
                        size={1920}
                        alt={article.title}
                        className="w-full h-full object-cover opacity-50"
                    />
                </div>
            </div>
            
            <div className="container mx-auto px-6 max-w-3xl">
                <div className="relative bg-brand-light p-8 md:p-12 shadow-lg z-10 -mt-32">
                    <div className="mb-8 font-header">
                        <Link to="/journal" className="text-sm text-gray-500 hover:text-brand-dark transition-colors">
                            &larr; Back to Journal
                        </Link>
                    </div>

                    <div className="text-center mb-8">
                         <h1 className="text-4xl md:text-5xl font-extrabold my-4 tracking-tight font-heading">{article.title}</h1>
                        <div className="flex items-center justify-center gap-3 text-sm uppercase tracking-widest text-gray-500 font-header">
                            <OptimizedImage
                                src={article.author.avatarUrl}
                                size={200}
                                alt={article.author.name}
                                className="w-8 h-8 rounded-full object-cover"
                                fallbackSrc={`https://ui-avatars.com/api/?name=${encodeURIComponent(article.author.name)}&background=e5e7eb&color=4b5563&size=64`}
                            />
                            <span className="font-semibold">By {article.author.name}</span>
                            <span>&bull;</span>
                            <span>{formattedDate}</span>
                        </div>
                    </div>

                    <div 
                        className="prose prose-lg max-w-none text-gray-800 leading-relaxed font-body"
                        dangerouslySetInnerHTML={{ __html: article.content }}
                    >
                    </div>
                </div>

                 <div className="mt-16 border-t pt-8 text-center font-header">
                    <Link to="/journal" className="inline-block px-6 py-3 text-sm font-semibold bg-gray-100 text-brand-dark hover:bg-gray-200 transition-colors">
                        Explore More Articles
                    </Link>
                </div>
            </div>
             <style>{`
                .prose p { margin-bottom: 1.25em; }
                .prose h1, .prose h2, .prose h3, .prose h4, .prose h5, .prose h6 { font-family: var(--font-family-heading); font-weight: 700; margin-top: 2em; margin-bottom: 1em; }
                .prose ul, .prose ol { margin-left: 1.5em; }
                .prose a { color: inherit; }
                .prose img { margin-top: 2em; margin-bottom: 2em; max-width: 100%; height: auto; border-radius: 0.25rem; }
                .prose { font-family: var(--font-family-body); }
            `}</style>
        </div>
    );
};

export default ArticleDetailPage;